import { Nav } from "./components/site/Nav";
import { Footer } from "./components/site/Footer";
import logo from "./assets/mikazura-logo.png";
import ed1 from "./assets/editorial-1.jpg";
import ed2 from "./assets/editorial-2.jpg";
import ed3 from "./assets/editorial-3.jpg";
import ed4 from "./assets/editorial-4.jpg";
import ed5 from "./assets/editorial-5.jpg";
import ed6 from "./assets/editorial-6.jpg";

const aesthetics = [
  { name: "Noir Cinema", desc: "Black & white grain. Shadow play. The frame as confession.", img: ed3 },
  { name: "Cyber Gothic", desc: "Chrome chains, neon bleed, rain on asphalt at 2am.", img: ed2 },
  { name: "Gothic Romance", desc: "Velvet, candlelight, oxblood lips. Painted like a memory.", img: ed4 },
  { name: "Chrome Future", desc: "Liquid silver. Editorial cyborg. Magazine cover energy.", img: ed5 },
  { name: "Underground Street", desc: "Flash photography in alley light. Worn leather. Defiance.", img: ed6 },
  { name: "Dark Editorial", desc: "Studio noir. Silver jewelry. Direct gaze. No apology.", img: ed1 },
];

const marqueeWords = [
  "DARK EDITORIAL",
  "MONOCHROME LUXURY",
  "CYBERPUNK",
  "GOTHIC ROMANCE",
  "NOIR CINEMA",
  "CHROME AESTHETICS",
  "UNDERGROUND FASHION",
  "MOODY STREETWEAR",
];

function App() {
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
      <Nav />

      {/* HERO */}
      <section className="relative grain min-h-screen w-full overflow-hidden bg-noir pt-24">
        <div className="mx-auto grid max-w-[1600px] grid-cols-12 gap-6 px-6 pb-16 md:px-12 md:pb-24">
          <div className="reveal-up relative z-10 col-span-12 flex flex-col justify-end pt-8 md:col-span-7 md:pt-24">
            <p className="text-[11px] uppercase tracking-editorial text-chrome flicker">
              ⬤ Now accepting Spring/Summer commissions
            </p>
            <h1 className="mt-8 font-display text-[clamp(3.5rem,9vw,9rem)] leading-[0.88] tracking-mega text-bone">
              Your Face,
              <br />
              <em className="font-serif-soft italic text-chrome-grad">Reimagined</em>
              <br />
              as Cinema.
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Mikazura is an AI photoshoot studio crafting editorial portraits with atmosphere — dark, cinematic, unforgettable. Send a face. Receive a universe.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#book"
                className="group relative inline-flex items-center gap-3 bg-bone px-7 py-4 text-[11px] uppercase tracking-editorial text-background transition-all hover:bg-chrome"
              >
                Begin Your Shoot
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
              <a
                href="#gallery"
                className="text-[11px] uppercase tracking-editorial text-bone underline-offset-8 hover:underline"
              >
                Enter the Gallery
              </a>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-6 border-t border-border/60 pt-8 max-w-md">
              <Stat n="04" label="Aesthetic Phases" />
              <Stat n="∞" label="Identities" />
              <Stat n="48h" label="Delivery" />
            </div>
          </div>

          <div className="relative col-span-12 md:col-span-5">
            <div className="relative aspect-[3/4] w-full overflow-hidden bg-card shadow-cinema">
              <img
                src={ed1}
                alt="Mikazura editorial portrait"
                width={1024}
                height={1408}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-[10px] uppercase tracking-editorial text-bone/80">
                <span>Issue n°01 — Noir</span>
                <span>MKZ / 25</span>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-4 hidden h-32 w-32 border border-chrome/40 bg-background/60 backdrop-blur-sm md:flex flex-col items-center justify-center text-center">
              <p className="font-display text-3xl text-chrome">M</p>
              <p className="mt-1 text-[9px] uppercase tracking-editorial text-muted-foreground">Mikazura<br/>Studio</p>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative border-y border-border/40 bg-background/40 py-4 backdrop-blur-sm">
          <div className="flex w-max marquee gap-12 whitespace-nowrap text-[11px] uppercase tracking-editorial text-muted-foreground">
            {[...marqueeWords, ...marqueeWords, ...marqueeWords, ...marqueeWords].map((w, i) => (
              <span key={i} className="flex items-center gap-12">
                {w}
                <span className="text-oxblood">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="relative border-b border-border/40 bg-background py-24 md:py-40">
        <div className="mx-auto grid max-w-[1400px] grid-cols-12 gap-6 px-6 md:px-12">
          <p className="col-span-12 text-[11px] uppercase tracking-editorial text-chrome md:col-span-3">
            ⟶ Manifesto
          </p>
          <div className="col-span-12 md:col-span-9">
            <p className="font-display text-[clamp(1.75rem,3.4vw,3rem)] leading-[1.05] tracking-mega text-bone">
              Photography should feel like <em className="italic font-serif-soft text-chrome">art direction</em> — not just pictures. In a world flooded with repetition, Mikazura makes images with atmosphere: portraits that feel like <em className="italic font-serif-soft text-chrome">movie stills</em>, fashion editorials, moments from another reality.
            </p>
            <p className="mt-10 max-w-2xl text-base leading-relaxed text-muted-foreground">
              No expensive sets. No massive crews. Just imagination, identity, and aesthetic storytelling. For people who want to be seen differently — not polished into perfection, but transformed into something cinematic.
            </p>
          </div>
        </div>
      </section>

      {/* AESTHETICS */}
      <section id="aesthetics" className="relative bg-background py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="text-[11px] uppercase tracking-editorial text-chrome">⟶ Aesthetic Universes</p>
              <h2 className="mt-4 font-display text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] tracking-mega text-bone">
                Choose your<br/><em className="italic font-serif-soft text-chrome-grad">main character</em> era.
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
              Six signature directions. Endless permutations. Every shoot is custom — built around your face, your mood, your universe.
            </p>
          </div>

          <div className="mt-16 grid gap-px bg-border/40 md:grid-cols-3">
            {aesthetics.map((a, i) => (
              <article key={a.name} className="group relative aspect-[4/5] overflow-hidden bg-card">
                <img
                  src={a.img}
                  alt={a.name}
                  width={1024}
                  height={1280}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent opacity-90 transition-opacity group-hover:opacity-95" />
                <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-8">
                  <div className="flex items-start justify-between text-[10px] uppercase tracking-editorial text-bone/70">
                    <span>0{i + 1}</span>
                    <span>MKZ</span>
                  </div>
                  <div>
                    <h3 className="font-display text-3xl leading-tight tracking-mega text-bone md:text-4xl">
                      {a.name}
                    </h3>
                    <p className="mt-3 max-w-xs text-sm text-muted-foreground">{a.desc}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-editorial text-chrome">
                      Explore series →
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="relative border-y border-border/40 bg-background py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="text-[11px] uppercase tracking-editorial text-chrome">⟶ The Process</p>
          <h2 className="mt-4 font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-mega text-bone">
            Four steps from <em className="italic font-serif-soft text-chrome">photo</em> to <em className="italic font-serif-soft text-chrome">film still</em>.
          </h2>

          <div className="mt-16 grid gap-8 md:grid-cols-4">
            {[
              { n: "01", t: "Choose Aesthetic", d: "Pick a universe — noir, chrome, gothic, cyber, soft luxury, or build your own." },
              { n: "02", t: "Send Your Face", d: "Upload a handful of photos and any references that shape your mood." },
              { n: "03", t: "Direction", d: "We treat your face like art direction — lighting, wardrobe, atmosphere, story." },
              { n: "04", t: "Receive Cinema", d: "Final, fully edited editorial portraits delivered within 48 hours." },
            ].map((step) => (
              <div key={step.n} className="border-t border-border pt-6">
                <p className="font-display text-5xl tracking-mega text-chrome-grad">{step.n}</p>
                <h3 className="mt-6 font-display text-xl text-bone">{step.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY editorial */}
      <section id="gallery" className="relative bg-background py-24 md:py-40">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-4">
              <p className="text-[11px] uppercase tracking-editorial text-chrome">⟶ Editorial</p>
              <h2 className="mt-4 font-display text-[clamp(2.25rem,4.5vw,4rem)] leading-[0.95] tracking-mega text-bone">
                Issue<br/><span className="text-chrome-grad">n°01</span>
              </h2>
              <p className="mt-6 max-w-xs text-sm text-muted-foreground">
                A study in shadow, chrome, and the quiet violence of a perfect frame.
              </p>
              <p className="mt-12 text-[10px] uppercase tracking-editorial text-muted-foreground">
                Direction — Mikazura<br/>
                Lighting — Cinematic flash<br/>
                Wardrobe — Black leather, silver
              </p>
            </div>

            <div className="col-span-12 grid grid-cols-2 gap-3 md:col-span-8 md:grid-cols-3 md:gap-4">
              <div className="col-span-2 row-span-2 aspect-[3/4] overflow-hidden bg-card md:col-span-2">
                <img src={ed2} alt="" loading="lazy" width={1024} height={1280} className="h-full w-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden bg-card">
                <img src={ed3} alt="" loading="lazy" width={1024} height={1280} className="h-full w-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden bg-card">
                <img src={ed5} alt="" loading="lazy" width={1024} height={1280} className="h-full w-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden bg-card">
                <img src={ed4} alt="" loading="lazy" width={1024} height={1280} className="h-full w-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden bg-card">
                <img src={ed6} alt="" loading="lazy" width={1024} height={1280} className="h-full w-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden bg-card">
                <img src={ed1} alt="" loading="lazy" width={1024} height={1280} className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section id="vision" className="relative overflow-hidden border-t border-border/40 bg-oxblood-grad py-24 md:py-32">
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="text-[11px] uppercase tracking-editorial text-bone/70">⟶ The Long View</p>
          <h2 className="mt-4 font-display text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] tracking-mega text-bone">
            From digital studio to <em className="italic font-serif-soft">creative production house.</em>
          </h2>

          <div className="mt-16 grid gap-px bg-bone/10 md:grid-cols-4">
            {[
              { p: "Phase 01", t: "AI Photoshoot Brand", n: "Now" },
              { p: "Phase 02", t: "Creative Direction & Styling", n: "Soon" },
              { p: "Phase 03", t: "Physical Luxury Studio", n: "Next" },
              { p: "Phase 04", t: "Full Production House", n: "Future" },
            ].map((p) => (
              <div key={p.p} className="bg-background/80 p-6 md:p-8">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-editorial text-chrome">
                  <span>{p.p}</span>
                  <span>{p.n}</span>
                </div>
                <p className="mt-12 font-display text-2xl leading-tight text-bone">{p.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="book" className="relative bg-background py-24 md:py-40">
        <div className="mx-auto max-w-[1400px] px-6 text-center md:px-12">
          <img src={logo} alt="Mikazura" width={120} height={120} className="mx-auto h-24 w-auto opacity-80" />
          <h2 className="mt-10 font-display text-[clamp(3rem,8vw,8rem)] leading-[0.9] tracking-mega text-bone">
            Enter your<br/><em className="italic font-serif-soft text-chrome-grad">main character</em><br/>era.
          </h2>
          <p className="mx-auto mt-8 max-w-xl text-base text-muted-foreground">
            Limited commissions per month. Each shoot directed personally — built around your face, your mood, your universe.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <a
              href="mailto:hello@mikazura.studio"
              className="group inline-flex items-center gap-3 bg-bone px-8 py-4 text-[11px] uppercase tracking-editorial text-background transition-all hover:bg-chrome"
            >
              Request a Shoot
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a href="#" className="inline-flex items-center gap-3 border border-bone/30 px-8 py-4 text-[11px] uppercase tracking-editorial text-bone hover:border-bone">
              Follow on Instagram
            </a>
          </div>
          <p className="mt-12 text-[10px] uppercase tracking-editorial text-muted-foreground">
            Curated for the beautifully uncommon
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div>
      <p className="font-display text-3xl tracking-mega text-chrome-grad">{n}</p>
      <p className="mt-2 text-[10px] uppercase tracking-editorial text-muted-foreground">{label}</p>
    </div>
  );
}

export default App;
